import React, { memo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import * as S from './PandaDocAnalytics.styled';
import {
  COURSE_OPTIONS,
  DatePresetId,
  GRANULARITY_OPTIONS,
  Granularity,
} from './constants';
import { DateRangeFilter } from './DateRangeFilter';

interface IKpiCardProps {
  label: string;
  value: ReactNode;
  subLabel?: ReactNode;
  accent?: string;
}

/** A single KPI card — label, big value, optional sub-label. */
export const KpiCard = memo(
  ({ label, value, subLabel, accent }: IKpiCardProps) => (
    <S.KpiCardBox>
      <S.KpiLabel>{label}</S.KpiLabel>
      <S.KpiValue accent={accent}>{value}</S.KpiValue>
      {subLabel !== undefined && <S.KpiSubLabel>{subLabel}</S.KpiSubLabel>}
    </S.KpiCardBox>
  ),
);

interface IChartCardProps {
  title: string;
  dotColor?: string;
  meta?: ReactNode;
  legend?: ReactNode;
  tall?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}

/** Card shell around a chart — title with optional color dot, meta, footer. */
export const ChartCard = memo(
  ({
    title,
    dotColor,
    meta,
    legend,
    tall,
    footer,
    children,
  }: IChartCardProps) => (
    <S.Card>
      <S.CardHeader>
        <S.CardTitle>
          {dotColor && <S.Dot color={dotColor} />}
          {title}
        </S.CardTitle>
        {legend}
        {meta !== undefined && <S.CardMeta>{meta}</S.CardMeta>}
      </S.CardHeader>
      <S.ChartWrap tall={tall}>{children}</S.ChartWrap>
      {footer !== undefined && <S.FooterNote>{footer}</S.FooterNote>}
    </S.Card>
  ),
);

const GRANULARITY_LABEL_KEY: Record<Granularity, string> = {
  day: 'pages.pandadocAnalytics.filters.granularityDay',
  week: 'pages.pandadocAnalytics.filters.granularityWeek',
  month: 'pages.pandadocAnalytics.filters.granularityMonth',
};

interface IFiltersProps {
  granularity: Granularity;
  course: string;
  datePreset: DatePresetId | 'custom';
  dateFrom: string | null;
  dateTo: string | null;
  onGranularityChange: (value: Granularity) => void;
  onCourseChange: (value: string) => void;
  onDatePresetChange: (id: DatePresetId) => void;
  onDateCustomChange: (from: string | null, to: string | null) => void;
}

/** Global filter bar — date range, granularity and course. */
export const Filters = memo(
  ({
    granularity,
    course,
    datePreset,
    dateFrom,
    dateTo,
    onGranularityChange,
    onCourseChange,
    onDatePresetChange,
    onDateCustomChange,
  }: IFiltersProps) => {
    const { t } = useTranslation();

    return (
      <S.Controls>
        <DateRangeFilter
          preset={datePreset}
          from={dateFrom}
          to={dateTo}
          onPresetChange={onDatePresetChange}
          onCustomChange={onDateCustomChange}
        />
        <S.ControlGroup>
          <S.ControlLabel>
            {t('pages.pandadocAnalytics.filters.granularity')}
          </S.ControlLabel>
          <S.Tabs>
            {GRANULARITY_OPTIONS.map((value) => (
              <S.Tab
                key={value}
                type="button"
                active={granularity === value}
                onClick={() => onGranularityChange(value)}
              >
                {t(GRANULARITY_LABEL_KEY[value])}
              </S.Tab>
            ))}
          </S.Tabs>
        </S.ControlGroup>
        <S.ControlGroup>
          <S.ControlLabel>
            {t('pages.pandadocAnalytics.filters.course')}
          </S.ControlLabel>
          <S.PillGroup>
            {COURSE_OPTIONS.map((value) => (
              <S.Pill
                key={value}
                type="button"
                active={course === value}
                onClick={() => onCourseChange(value)}
              >
                {value === 'all'
                  ? t('pages.pandadocAnalytics.filters.courseAll')
                  : value}
              </S.Pill>
            ))}
          </S.PillGroup>
        </S.ControlGroup>
      </S.Controls>
    );
  },
);
